import { useState, useEffect } from 'react';
import { Input, Col, FormGroup, Label } from 'reactstrap';
import { startOfDay } from 'date-fns';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../../helpers/datePickerConfig';
import PropTypes from 'prop-types';
import { Search, ChevronDown, ChevronUp, ChevronLeft, CheckSquare, Square } from 'react-feather';
import { formatDate } from '../../helpers/dateUtils';
import Table from '../ui/Table';
import facturasApiService from '../../services/http/facturasApiService';
import ModalClickable from '../../layouts/modals/ModalClickable';
import logger from '../../services/logger';
import colors from '../../theme/colors';

const CheckboxReciboCell = ({ original, seleccionados, onCheckboxChange }) => (
  <Input
    type="checkbox"
    checked={seleccionados.includes(original.NUM_RECIBO)}
    onChange={() => onCheckboxChange(original.NUM_RECIBO)}
  />
);
CheckboxReciboCell.propTypes = {
  original: PropTypes.shape({
    NUM_RECIBO: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }).isRequired,
  seleccionados: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  onCheckboxChange: PropTypes.func.isRequired,
};

const RecibosFiltrados = ({
  headquarter,
  selectedClientes,
  ret,
  fechaFin,
  formaPago,
  fechaInicio,
  tipoUsuario,
}) => {
  const [showFilters, setShowFilters] = useState(true);
  const [isShown, setIsShown] = useState(false);
  const [dataRecibos, setDataRecibos] = useState([]);
  const [seleccionados, setSeleccionados] = useState([]);
  const [todosSeleccionados, setTodosSeleccionados] = useState(false);
  const [fechaVTO, setFechaVTO] = useState(null);
  const [data, setData] = useState('');

  const handleCheckboxChange = (numRecibo) => {
    setSeleccionados((prev) =>
      prev.includes(numRecibo) ? prev.filter((id) => id !== numRecibo) : [...prev, numRecibo],
    );
  };

  const totalRecibosSeleccionados = seleccionados.length;
  const totalImporteSeleccionado = seleccionados
    .map((numRecibo) => {
      const recibo = dataRecibos.find((r) => r.NUM_RECIBO === numRecibo);
      return recibo ? parseFloat(recibo.IMPORTE) : 0;
    })
    .reduce((acc, val) => acc + val, 0);

  const toggleSeleccionarTodos = () => {
    if (todosSeleccionados) {
      setSeleccionados([]);
      setTodosSeleccionados(false);
    } else {
      setSeleccionados(dataRecibos.map((item) => item.NUM_RECIBO));
      setTodosSeleccionados(true);
    }
  };

  const loadData = async () => {
    const filtros = {
      headquarter,
      ids: selectedClientes.toString(),
      fechaFin: fechaFin ? formatDate(fechaFin, 'yyyy-MM-dd') : null,
      fechaInicio: fechaInicio ? formatDate(fechaInicio, 'yyyy-MM-dd') : null,
      tipoUsuario,
      formaPago,
    };
    try {
      const filtered = await facturasApiService.obtenerFacturasFiltradas(filtros);
      setDataRecibos(filtered);
      setSeleccionados([]);
      setTodosSeleccionados(false);
    } catch (error) {
      logger.error('Error al buscar:', error);
    }
  };

  const handleOnClick = async (opcion) => {
    setIsShown(true);
    const filtros = {
      headquarter, ids: seleccionados.toString(), formaPago,
      tipoUsuario, fechaFin, fechaInicio, opcion, fechaVTO,
    };
    const d = await facturasApiService.controlarRecibos(filtros);
    setData(d);
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canProcess = seleccionados.length > 0 && fechaVTO !== null;

  const toolbarButtons = (
    <>
      {dataRecibos.length > 0 && (
        <button
          type="button"
          onClick={toggleSeleccionarTodos}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 12px',
            fontSize: '0.75rem', fontWeight: 600, borderRadius: '6px',
            border: '1px solid #e5e7eb', backgroundColor: '#fff', color: '#6b7280',
            cursor: 'pointer', transition: 'all 0.15s',
          }}
        >
          {todosSeleccionados ? <Square size={13} /> : <CheckSquare size={13} />}
          {todosSeleccionados ? 'Deseleccionar todos' : 'Seleccionar todos'}
        </button>
      )}
      <button
        type="button"
        onClick={ret}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 12px',
          fontSize: '0.75rem', fontWeight: 600, borderRadius: '6px',
          border: '1px solid #e5e7eb', backgroundColor: '#fff', color: '#374151',
          cursor: 'pointer', transition: 'all 0.15s',
        }}
      >
        <ChevronLeft size={13} />
        Atrás
      </button>
      <button
        type="button"
        onClick={() => handleOnClick(1)}
        disabled={!canProcess}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 12px',
          fontSize: '0.75rem', fontWeight: 600, borderRadius: '6px',
          border: `1px solid ${colors.primary}`, backgroundColor: '#fff', color: colors.primary,
          cursor: !canProcess ? 'not-allowed' : 'pointer',
          opacity: !canProcess ? 0.5 : 1, transition: 'all 0.15s',
        }}
      >
        Línea por recibo
      </button>
      <button
        type="button"
        onClick={() => handleOnClick(2)}
        disabled={!canProcess}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 12px',
          fontSize: '0.75rem', fontWeight: 600, borderRadius: '6px', border: 'none',
          backgroundColor: colors.primary, color: '#fff',
          cursor: !canProcess ? 'not-allowed' : 'pointer',
          opacity: !canProcess ? 0.5 : 1, transition: 'all 0.15s',
        }}
      >
        Agrupación recibos
      </button>
    </>
  );

  return (
    <>
      {isShown && (
        <ModalClickable
          color="primary"
          header="Actualizado."
          url="/facturar-recibos"
          totalImporte={totalImporteSeleccionado}
          totalRecibos={totalRecibosSeleccionados}
          facturasAgrupadas={data?.facturasAgrupadas}
        />
      )}

      <div style={{
        backgroundColor: '#fff', borderRadius: '12px',
        border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgb(0 0 0 / 0.06)',
        overflow: 'visible',
      }}>
        {/* Toolbar */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: showFilters ? '1px solid #f3f4f6' : 'none',
        }}>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: '0.8rem', fontWeight: 600, color: '#374151',
              padding: '4px 8px', borderRadius: '6px',
            }}
          >
            <Search size={15} style={{ color: '#9ca3af' }} />
            {`Recibos filtrados — ${headquarter}`}
            {showFilters
              ? <ChevronUp size={14} style={{ color: '#9ca3af' }} />
              : <ChevronDown size={14} style={{ color: '#9ca3af' }} />}
          </button>
          <div style={{ display: 'flex', gap: '8px' }}>
            {toolbarButtons}
          </div>
        </div>

        {/* Filtros colapsables */}
        {showFilters && (
          <div style={{ padding: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '24px', flexWrap: 'wrap' }}>
              <div>
                <Label>Fecha de vencimiento</Label>
                <FormGroup style={{ margin: 0 }}>
                  <DatePicker
                    selected={fechaVTO}
                    onChange={(date) => {
                      if (!date) { setFechaVTO(null); return; }
                      setFechaVTO(startOfDay(date));
                    }}
                    dateFormat="dd/MM/yyyy" locale="es"
                    className="form-control" wrapperClassName="w-100"
                  />
                </FormGroup>
              </div>
              {dataRecibos.length > 0 && (
                <div style={{ fontSize: '0.8rem', color: '#374151' }}>
                  <p style={{ margin: 0 }}>Total recibos seleccionados: <strong>{totalRecibosSeleccionados}</strong></p>
                  <p style={{ margin: 0 }}>Total importe: <strong>{totalImporteSeleccionado.toFixed(2)} €</strong></p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {dataRecibos.length > 0 && (
        <Col md="12" style={{ marginTop: '16px' }}>
          <Table
            columns={[
              {
                id: 'seleccionar', Header: 'Seleccionar', accessor: 'NUM_RECIBO',
                CellComponent: CheckboxReciboCell,
                cellProps: { seleccionados, onCheckboxChange: handleCheckboxChange },
                sortable: false,
              },
              { id: 'ccodcli', Header: 'ID', accessor: 'ccodcli' },
              { Header: 'Cliente', accessor: 'cliente' },
              { Header: 'Número recibo', accessor: 'NUM_RECIBO' },
              { Header: 'Fecha vencimiento', accessor: 'FECHA_VTO' },
              { Header: 'Forma de pago', accessor: 'Formadepago' },
              { Header: 'Concepto', accessor: 'concepto' },
              { Header: 'Neto', accessor: 'neto' },
              { Header: '%', accessor: '%' },
              { Header: 'Impuesto', accessor: 'impuesto' },
              { Header: 'Importe', accessor: 'IMPORTE' },
              { Header: 'Numero matrícula', accessor: 'nummatricula' },
            ]}
            data={dataRecibos}
            defaultPageSize={10}
            showPaginationBottom
            className="-striped -highlight text-center"
          />
        </Col>
      )}
    </>
  );
};

RecibosFiltrados.propTypes = {
  headquarter: PropTypes.string.isRequired,
  selectedClientes: PropTypes.arrayOf(PropTypes.string).isRequired,
  ret: PropTypes.func.isRequired,
  fechaFin: PropTypes.instanceOf(Date),
  formaPago: PropTypes.string,
  fechaInicio: PropTypes.instanceOf(Date),
  tipoUsuario: PropTypes.string,
};

export default RecibosFiltrados;
